import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { dedupeDiscoveries, dedupeQuizzes } from "./content-dedupe";

export type DBCategory = {
  id: string;
  slug: string;
  label: string;
  title: string;
  blurb: string;
  cover_url: string | null;
  sort_order: number;
};

export type DiscoverySource = { title: string; url: string };
export type ConfidenceLevel = "high" | "medium" | "speculative";

export type DBDiscovery = {
  id: string;
  slug: string;
  category_id: string | null;
  title: string;
  excerpt: string;
  image_url: string | null;
  views_label: string | null;
  is_featured: boolean;
  is_daily: boolean;
  is_deep_dive: boolean;
  is_reel: boolean;
  reel_hook: string | null;
  is_published: boolean;
  body: string | null;
  sort_order: number;
  created_at: string;
  sources: DiscoverySource[];
  confidence: ConfidenceLevel | null;
  publish_at: string | null;
  seo_title: string | null;
  seo_description: string | null;
  audio_url: string | null;
  audio_script?: string | null;
  mid_image_url: string | null;
  motion_video_url: string | null;
  image_gallery?: Array<{ url: string; concept?: string }> | null;
  hq_origin_reel_id?: string | null;
  hq_source?: string | null;
  reel_views?: number;
  reel_score?: number;
  reel_permalink?: string | null;
};

export type DBQuiz = {
  id: string;
  slug: string;
  kind: "guess" | "challenge";
  question: string;
  options: string[];
  answer_index: number;
  explanation: string;
  href: string | null;
  placement: string | null;
  sort_order: number;
  category?: string | null;
  difficulty_tier?: string | null;
};

export type HomepageContent = {
  categories: DBCategory[];
  discoveries: DBDiscovery[];
  quizzes: DBQuiz[];
  heroSlug: string | null;
  viralReels: DBDiscovery[];
  /** Real published totals - the homepage query is capped, so never count the array. */
  totals: { discoveries: number; collections: number };
  collections: CollectionSummary[];
  trending: TrendingItem[];
};

// Emotion buckets used to bias recommendations toward the same "vibe" as the
// current article (danger, mystery, awe, disgust, survival, impossible, etc.).
// Keyword match on title + excerpt - no schema change, no backfill.
const EMOTION_BUCKETS: Record<string, string[]> = {
  danger: [
    "kill",
    "deadly",
    "venom",
    "toxic",
    "poison",
    "attack",
    "dangerous",
    "lethal",
    "predator",
    "apex",
    "fatal",
    "weapon",
  ],
  mystery: [
    "mystery",
    "unknown",
    "unexplained",
    "strange",
    "weird",
    "secret",
    "hidden",
    "puzzle",
    "enigma",
    "vanish",
    "missing",
  ],
  awe: [
    "giant",
    "massive",
    "largest",
    "biggest",
    "record",
    "incredible",
    "impossible",
    "universe",
    "galaxy",
    "cosmic",
    "ancient",
  ],
  survival: [
    "survive",
    "survival",
    "extreme",
    "endure",
    "adapt",
    "harsh",
    "escape",
    "freeze",
    "desert",
    "alive",
  ],
  disgusting: [
    "parasite",
    "worm",
    "maggot",
    "slime",
    "rot",
    "decompose",
    "gross",
    "disgusting",
    "fungus",
    "mold",
    "corpse",
  ],
  impossible: [
    "impossible",
    "defies",
    "breaks",
    "law",
    "physics",
    "paradox",
    "cannot",
    "shouldnt",
    "logic-defying",
  ],
  origins: [
    "ancient",
    "first",
    "oldest",
    "evolved",
    "origin",
    "billion",
    "million",
    "fossil",
    "prehistoric",
    "dinosaur",
  ],
  micro: [
    "tiny",
    "microscopic",
    "microbe",
    "bacteria",
    "virus",
    "cell",
    "atom",
    "invisible",
    "smallest",
  ],
  ocean: [
    "ocean",
    "deep",
    "abyss",
    "trench",
    "sea",
    "marine",
    "underwater",
    "whale",
    "shark",
    "squid",
  ],
  human: ["brain", "body", "human", "organ", "blood", "heart", "dna", "gene", "skin", "nerve"],
};
function emotionTags(text: string): Set<string> {
  const t = text.toLowerCase();
  const out = new Set<string>();
  for (const [tag, words] of Object.entries(EMOTION_BUCKETS)) {
    if (words.some((w) => t.includes(w))) out.add(tag);
  }
  return out;
}

// Scheduled-publish gate: include articles with no publish_at, or whose publish_at has passed.
function scheduledFilter() {
  return `publish_at.is.null,publish_at.lte.${new Date(Math.floor(Date.now() / 300000) * 300000).toISOString()}`;
}

const INSTAGRAM_COLLECTION_PRIORITY = [
  "things-that-can-kill-you",
  "apex-predators",
  "animals-that-shouldnt-exist",
  "ocean-deep",
  "survival-superpowers",
  "the-tiny-and-terrifying",
];

let homepageCache: { data: HomepageContent; timestamp: number } | null = null;
const HOMEPAGE_CACHE_TTL = 10 * 60 * 1000;

export const getHomepageContent = createServerFn({ method: "GET" }).handler(
  async (): Promise<HomepageContent> => {
    if (homepageCache && Date.now() - homepageCache.timestamp < HOMEPAGE_CACHE_TTL)
      return homepageCache.data;
    const [{ supabaseAdmin }, { isPlaceholderText }] = await Promise.all([
      import("@/integrations/supabase/client.server"),
      import("./publish-quality"),
    ]);
    const [cats, discs, newestByPublish, newestByCreation, heroCfg, reelRows, totalDiscs, totalColls, colls, collItems] =
      await Promise.all([
        supabaseAdmin.from("categories").select("*").order("sort_order"),
        supabaseAdmin
          .from("discoveries")
          // Homepage cards need metadata, not full article/audio/gallery payloads.
          // Selecting every column forced PostgREST to de-TOAST and JSON-encode
          // every published article on each root request, which was the exact
          // query repeatedly cancelled by the database statement timeout.
          .select(
            "id,slug,category_id,title,excerpt,image_url,views_label,is_featured,is_daily,is_deep_dive,is_reel,reel_hook,is_published,sort_order,created_at,publish_at,motion_video_url,hq_origin_reel_id,hq_source,quality_score",
          )
          .eq("is_published", true)
          .or(scheduledFilter())
          // Defense-in-depth on top of the publish quality gate: the homepage
          // must never render a row that lacks the fields we promise users.
          .not("body", "is", null)
          .neq("body", "")
          .not("image_url", "is", null)
          .neq("image_url", "")
          .not("category_id", "is", null)
          .order("sort_order")
          // The homepage shows a curated sample, not the whole catalogue.
          // Forty-eight rows leave room for every visible shelf while avoiding a large
          // hidden hydration payload on every visit.
          .limit(48),
        // Freshness is independent from editorial shelf order. Fetch both
        // publication-time and creation-time candidates so newly published
        // work can never be pushed out by old, manually pinned rows.
        supabaseAdmin
          .from("discoveries")
          .select(
            "id,slug,category_id,title,excerpt,image_url,views_label,is_featured,is_daily,is_deep_dive,is_reel,reel_hook,is_published,sort_order,created_at,publish_at,motion_video_url,hq_origin_reel_id,hq_source,quality_score",
          )
          .eq("is_published", true)
          .or(scheduledFilter())
          .not("body", "is", null)
          .neq("body", "")
          .not("image_url", "is", null)
          .neq("image_url", "")
          .not("category_id", "is", null)
          .not("publish_at", "is", null)
          .order("publish_at", { ascending: false })
          .limit(12),
        supabaseAdmin
          .from("discoveries")
          .select(
            "id,slug,category_id,title,excerpt,image_url,views_label,is_featured,is_daily,is_deep_dive,is_reel,reel_hook,is_published,sort_order,created_at,publish_at,motion_video_url,hq_origin_reel_id,hq_source,quality_score",
          )
          .eq("is_published", true)
          .or(scheduledFilter())
          .not("body", "is", null)
          .neq("body", "")
          .not("image_url", "is", null)
          .neq("image_url", "")
          .not("category_id", "is", null)
          .order("created_at", { ascending: false })
          .limit(12),
        supabaseAdmin
          .from("homepage_hero_config")
          .select("hero_slug,override_slug,override_active")
          .eq("id", 1)
          .maybeSingle(),
        supabaseAdmin
          .from("intel_entities")
          .select(
            "id,permalink,linked_discovery_id,intel_reel_scores(views,likes,comments,saves,shares,plays)",
          )
          .not("linked_discovery_id", "is", null)
          .limit(150),
        // Real published totals - the discovery query above is capped at 300,
        // so the array length would understate the library on the homepage.
        supabaseAdmin
          .from("discoveries")
          .select("id", { count: "exact", head: true })
          .eq("is_published", true),
        supabaseAdmin
          .from("discovery_collections")
          .select("id", { count: "exact", head: true })
          .neq("slug", "unclassified"),
        supabaseAdmin
          .from("discovery_collections")
          .select("id,slug,title,description,cover_url,sort_order,created_at")
          .neq("slug", "unclassified")
          .order("sort_order"),
        supabaseAdmin
          .from("collection_items")
          .select("collection_id,position,discoveries!inner(slug,image_url,is_published)")
          .order("position")
          .limit(400),
      ]);
    if (cats.error) throw new Error(cats.error.message);
    if (discs.error) throw new Error(discs.error.message);
    const freshRows = [...((newestByPublish.data ?? []) as any[]), ...((newestByCreation.data ?? []) as any[])];
    const allRows = [...freshRows, ...((discs.data ?? []) as any[])];
    const dedupedRows = [...new Map(allRows.map((d) => [d.id, d])).values()];
    const rawDiscs = dedupedRows.filter(
      (d) => !isPlaceholderText(d.title) && !isPlaceholderText(d.excerpt),
    );
    const scoreByReel = new Map<string, number>();
    for (const row of (reelRows.data ?? []) as any[]) {
      const scoreRow = Array.isArray(row.intel_reel_scores)
        ? row.intel_reel_scores[0]
        : row.intel_reel_scores;
      const views = Number(scoreRow?.views ?? scoreRow?.plays ?? 0);
      const score =
        views +
        Number(scoreRow?.likes ?? 0) * 8 +
        Number(scoreRow?.comments ?? 0) * 60 +
        Number(scoreRow?.saves ?? 0) * 80 +
        Number(scoreRow?.shares ?? 0) * 100;
      scoreByReel.set(row.id, score);
    }
    const cleanDiscs = dedupeDiscoveries([...rawDiscs]
      .sort((a, b) => {
        const ar = a.hq_origin_reel_id ? (scoreByReel.get(a.hq_origin_reel_id) ?? 0) : 0;
        const br = b.hq_origin_reel_id ? (scoreByReel.get(b.hq_origin_reel_id) ?? 0) : 0;
        if (br !== ar) return br - ar;
        return +new Date(b.created_at ?? 0) - +new Date(a.created_at ?? 0);
      }));
    const cfg = heroCfg?.data as {
      hero_slug: string | null;
      override_slug: string | null;
      override_active: boolean;
    } | null;
    const heroSlug =
      cfg?.override_active && cfg?.override_slug ? cfg.override_slug : (cfg?.hero_slug ?? null);
    const discoveryById = new Map(cleanDiscs.map((d) => [d.id, d]));
    const viralReels = ((reelRows.data ?? []) as any[])
      .map((row) => {
        const discovery = discoveryById.get(row.linked_discovery_id);
        if (!discovery?.image_url) return null;
        const scoreRow = Array.isArray(row.intel_reel_scores)
          ? row.intel_reel_scores[0]
          : row.intel_reel_scores;
        const views = Number(scoreRow?.views ?? scoreRow?.plays ?? 0);
        const score = scoreByReel.get(row.id) ?? views;
        return {
          ...discovery,
          hq_origin_reel_id: row.id,
          reel_permalink: row.permalink ?? null,
          reel_views: views,
          reel_score: score,
        } as DBDiscovery;
      })
      .filter((d): d is DBDiscovery => !!d)
      .sort((a, b) => (b.reel_score ?? 0) - (a.reel_score ?? 0));
    const collectionItems = new Map<string, Array<{ slug: string; image: string | null }>>();
    for (const row of (collItems.data ?? []) as any[]) {
      if (!row.discoveries?.is_published) continue;
      const arr = collectionItems.get(row.collection_id) ?? [];
      arr.push({ slug: row.discoveries.slug, image: row.discoveries.image_url ?? null });
      collectionItems.set(row.collection_id, arr);
    }
    const collections = ((colls.data ?? []) as any[])
      .map((c): CollectionSummary => {
        const arr = collectionItems.get(c.id) ?? [];
        return {
          id: c.id,
          slug: c.slug,
          title: c.title,
          description: c.description,
          cover_url: c.cover_url,
          count: arr.length,
          first_slug: arr[0]?.slug ?? null,
          first_image: c.cover_url ?? arr[0]?.image ?? null,
          sort_order: c.sort_order ?? 0,
          created_at: c.created_at ?? null,
        };
      })
      .filter((c) => c.count > 0)
      .sort((a, b) => {
        const ap = INSTAGRAM_COLLECTION_PRIORITY.indexOf(a.slug);
        const bp = INSTAGRAM_COLLECTION_PRIORITY.indexOf(b.slug);
        if (ap !== -1 || bp !== -1) return (ap === -1 ? 999 : ap) - (bp === -1 ? 999 : bp);
        return a.sort_order - b.sort_order;
      });
    // The homepage only needs a compact ranked shelf. Reel engagement already
    // arrives in this request; avoid downloading thousands of raw view rows.
    const trending = cleanDiscs
      .map((d) => ({
        d,
        count: d.hq_origin_reel_id ? (scoreByReel.get(d.hq_origin_reel_id) ?? 0) : 0,
      }))
      .sort(
        (a, b) =>
          b.count - a.count || +new Date(b.d.created_at ?? 0) - +new Date(a.d.created_at ?? 0),
      )
      .slice(0, 10)
      .map(({ d, count }) => ({
        id: d.id,
        slug: d.slug,
        title: d.title,
        excerpt: d.excerpt,
        image_url: d.image_url,
        category_id: d.category_id,
        views_label: d.views_label,
        view_count_7d: count,
      }));
    const result: HomepageContent = {
      categories: (cats.data ?? []) as DBCategory[],
      discoveries: cleanDiscs as unknown as DBDiscovery[],
      quizzes: [],
      heroSlug,
      viralReels,
      totals: {
        discoveries: totalDiscs.count ?? cleanDiscs.length,
        collections: totalColls.count ?? 0,
      },
      collections,
      trending,
    };
    homepageCache = { data: result, timestamp: Date.now() };
    return result;
  },
);

export const getDiscoveryBySlug = createServerFn({ method: "GET" })
  .inputValidator((d) =>
    z
      .object({
        slug: z.string().min(1).max(200),
        collectionSlug: z.string().min(1).max(200).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: disc, error } = await supabaseAdmin
      .from("discoveries")
      .select("*")
      .eq("slug", data.slug)
      .eq("is_published", true)
      .or(scheduledFilter())
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!disc) return null;
    const [cat, siblings, crossCat, allCats, quizzesRes] = await Promise.all([
      disc.category_id
        ? supabaseAdmin.from("categories").select("*").eq("id", disc.category_id).maybeSingle()
        : Promise.resolve({ data: null, error: null }),
      disc.category_id
        ? supabaseAdmin
            .from("discoveries")
            .select(
              "id,slug,title,image_url,excerpt,sort_order,created_at,views_label,quality_score,body",
            )
            .eq("is_published", true)
            .or(scheduledFilter())
            .eq("category_id", disc.category_id)
            .order("sort_order")
        : Promise.resolve({ data: [], error: null }),
      // Cross-category candidates: pull a wider pool, then rank by relevance below.
      disc.category_id
        ? supabaseAdmin
            .from("discoveries")
            .select(
              "id,slug,title,image_url,excerpt,category_id,created_at,views_label,quality_score,body",
            )
            .eq("is_published", true)
            .or(scheduledFilter())
            .neq("category_id", disc.category_id)
            .order("created_at", { ascending: false })
            .limit(40)
        : Promise.resolve({ data: [], error: null }),
      supabaseAdmin.from("categories").select("*").order("sort_order"),
      // Only quizzes that belong to THIS discovery. A quiz from another
      // article (or a random library quiz) reads as nonsense under the video.
      supabaseAdmin
        .from("quizzes")
        .select("*")
        .neq("placement", "certify")
        .eq("href", `/discovery/${data.slug}`)
        .order("sort_order"),
    ]);
    const sibs = (siblings?.data ?? []) as Array<
      Pick<
        DBDiscovery,
        | "id"
        | "slug"
        | "title"
        | "image_url"
        | "excerpt"
        | "sort_order"
        | "created_at"
        | "views_label"
      > & { quality_score?: number | null; body?: string | null }
    >;
    const idx = sibs.findIndex((s) => s.id === disc.id);
    const prev = idx > 0 ? sibs[idx - 1] : null;
    const next = idx >= 0 && idx < sibs.length - 1 ? sibs[idx + 1] : null;
    // Ranking priorities (highest → lowest weight):
    //   1. Emotional similarity (same vibe = "one more like this")
    //   2. Topical/token overlap
    //   3. Quality score
    //   4. Freshness (recency)
    // Category is only used as a candidate pool, not as a score.
    const STOP = new Set([
      "the",
      "a",
      "an",
      "and",
      "or",
      "of",
      "in",
      "on",
      "to",
      "for",
      "with",
      "is",
      "are",
      "was",
      "were",
      "be",
      "by",
      "at",
      "as",
      "it",
      "its",
      "this",
      "that",
      "these",
      "those",
      "you",
      "your",
      "we",
      "our",
      "from",
      "into",
      "but",
      "not",
      "no",
      "do",
      "does",
      "did",
      "can",
      "will",
      "why",
      "how",
      "what",
      "when",
      "where",
      "who",
      "whose",
      "which",
      "than",
      "then",
      "there",
      "they",
      "them",
    ]);
    const tokenize = (s: string) =>
      (s.toLowerCase().match(/[a-z0-9]+/g) ?? []).filter((t) => t.length > 2 && !STOP.has(t));
    const baseText = `${disc.title} ${disc.excerpt ?? ""} ${(disc.body ?? "").slice(0, 800)}`;
    const baseTokens = new Set(tokenize(baseText));
    const baseEmotions = emotionTags(baseText);
    const nowMs = Date.now();
    const scoreItem = (
      t: string,
      e: string | null,
      extra?: { body?: string | null; quality_score?: number | null; created_at?: string | null },
    ) => {
      const txt = `${t} ${e ?? ""} ${(extra?.body ?? "").slice(0, 800)}`;
      const toks = new Set(tokenize(txt));
      let topical = 0;
      for (const tok of toks) if (baseTokens.has(tok)) topical++;
      const emo = emotionTags(txt);
      let emoOverlap = 0;
      emo.forEach((tag) => {
        if (baseEmotions.has(tag)) emoOverlap++;
      });
      const quality = Math.max(0, Math.min(10, extra?.quality_score ?? 0));
      const ageDays = extra?.created_at
        ? Math.max(0, (nowMs - new Date(extra.created_at).getTime()) / 86_400_000)
        : 999;
      const freshness = Math.max(0, 30 - Math.min(30, ageDays)); // 0..30
      // Weights: emotion 6, topical 2, quality 1.5, freshness 0.1
      return emoOverlap * 6 + topical * 2 + quality * 1.5 + freshness * 0.1;
    };
    const related = sibs
      .filter((s) => s.id !== disc.id)
      .map((s) => ({
        s,
        score: scoreItem(s.title, s.excerpt ?? null, {
          body: (s as any).body,
          quality_score: (s as any).quality_score,
          created_at: s.created_at,
        }),
      }))
      .sort(
        (a, b) => b.score - a.score || (b.s.created_at ?? "").localeCompare(a.s.created_at ?? ""),
      )
      .slice(0, 6)
      .map((x) => x.s);

    // Collection context: prefer the collection the reader arrived from
    // (when provided via `collectionSlug`) so a discovery that belongs to
    // multiple collections keeps its Journey context - Discovery X of Y,
    // Previous/Next, progress, completion - instead of silently falling
    // out of the Journey. When no preference is supplied (or the
    // preferred collection doesn't contain this discovery), fall back to
    // the first collection by natural order.
    const { data: myCollItems } = await supabaseAdmin
      .from("collection_items")
      .select(
        "collection_id, position, discovery_collections!inner(id, slug, title, description, cover_url)",
      )
      .eq("discovery_id", disc.id)
      .order("position");
    let collectionContext: {
      collection: {
        id: string;
        slug: string;
        title: string;
        description: string | null;
        cover_url: string | null;
      };
      items: Array<{ id: string; slug: string; title: string; image_url: string | null }>;
      currentIndex: number;
    } | null = null;
    if (myCollItems && myCollItems.length > 0) {
      const preferred =
        data.collectionSlug &&
        (myCollItems as any[]).find(
          (r) => (r.discovery_collections as any)?.slug === data.collectionSlug,
        );
      const chosen = (preferred ?? myCollItems[0]) as any;
      const collId = chosen.collection_id as string;
      const coll = chosen.discovery_collections as any;
      const { data: ci } = await supabaseAdmin
        .from("collection_items")
        .select("position, discoveries!inner(id, slug, title, image_url, is_published)")
        .eq("collection_id", collId)
        .order("position");
      const items = (ci ?? [])
        .map((r: any) => r.discoveries)
        .filter((d: any) => d?.is_published)
        .map((d: any) => ({ id: d.id, slug: d.slug, title: d.title, image_url: d.image_url }));
      const idxInColl = items.findIndex((x) => x.id === disc.id);
      collectionContext = {
        collection: {
          id: coll.id,
          slug: coll.slug,
          title: coll.title,
          description: coll.description,
          cover_url: coll.cover_url,
        },
        items,
        currentIndex: idxInColl,
      };
    }
    // Strictly this discovery's own quiz - never a generic library fallback.
    const quiz = ((quizzesRes?.data ?? []) as DBQuiz[])[0] ?? null;

    // Instagram bridge: if this discovery was seeded from a viral reel,
    // hydrate the origin reel so the page can tell the reader "you're
    // reading the complete story behind the reel you saw".
    let originReel: {
      permalink: string | null;
      views: number | null;
      likes: number | null;
      ext_id: string | null;
    } | null = null;
    if ((disc as any).hq_origin_reel_id) {
      const [reelRes, scoreRes] = await Promise.all([
        supabaseAdmin
          .from("intel_entities")
          .select("permalink,ext_id")
          .eq("id", (disc as any).hq_origin_reel_id)
          .maybeSingle(),
        supabaseAdmin
          .from("intel_reel_scores")
          .select("views,likes")
          .eq("entity_id", (disc as any).hq_origin_reel_id)
          .maybeSingle(),
      ]);
      if (reelRes.data?.permalink) {
        originReel = {
          permalink: reelRes.data.permalink,
          ext_id: reelRes.data.ext_id ?? null,
          views: scoreRes.data?.views ?? null,
          likes: scoreRes.data?.likes ?? null,
        };
      }
    }
    return {
      discovery: disc as unknown as DBDiscovery,
      category: (cat?.data ?? null) as DBCategory | null,
      related: related as unknown as DBDiscovery[],
      crossCategory: ((crossCat?.data ?? []) as any[])
        .map((d) => ({
          d,
          score: scoreItem(d.title, d.excerpt ?? null, {
            body: d.body,
            quality_score: d.quality_score,
            created_at: d.created_at,
          }),
        }))
        .sort(
          (a, b) => b.score - a.score || (b.d.created_at ?? "").localeCompare(a.d.created_at ?? ""),
        )
        .slice(0, 4)
        .map((x) => x.d) as unknown as DBDiscovery[],
      categories: ((allCats?.data ?? []) as DBCategory[]).filter((c) => c.id !== disc.category_id),
      prev,
      next,
      collectionContext,
      quiz,
      originReel,
    };
  });

export type CollectionSummary = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  cover_url: string | null;
  count: number;
  first_slug: string | null;
  first_image: string | null;
  sort_order: number;
  created_at: string | null;
};

export const getCollections = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ items: CollectionSummary[] }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: colls } = await supabaseAdmin
      .from("discovery_collections")
      .select("*")
      .neq("slug", "unclassified")
      .order("sort_order");
    const { data: items } = await supabaseAdmin
      .from("collection_items")
      .select("collection_id, position, discoveries!inner(slug, image_url, is_published)")
      .order("position");
    const byColl = new Map<string, Array<{ slug: string; image: string | null }>>();
    for (const r of (items ?? []) as any[]) {
      if (!r.discoveries?.is_published) continue;
      const arr = byColl.get(r.collection_id) ?? [];
      arr.push({ slug: r.discoveries.slug, image: r.discoveries.image_url ?? null });
      byColl.set(r.collection_id, arr);
    }
    const out: CollectionSummary[] = (colls ?? []).map((c: any) => {
      const arr = byColl.get(c.id) ?? [];
      return {
        id: c.id,
        slug: c.slug,
        title: c.title,
        description: c.description,
        cover_url: c.cover_url,
        count: arr.length,
        first_slug: arr[0]?.slug ?? null,
        first_image: c.cover_url ?? arr[0]?.image ?? null,
        sort_order: c.sort_order ?? 0,
        created_at: c.created_at ?? null,
      };
    });
    // Hide empty collections from public listings - empty shells look broken.
    return {
      items: out
        .filter((c) => c.count > 0)
        .sort((a, b) => {
          const ap = INSTAGRAM_COLLECTION_PRIORITY.indexOf(a.slug);
          const bp = INSTAGRAM_COLLECTION_PRIORITY.indexOf(b.slug);
          if (ap !== -1 || bp !== -1) return (ap === -1 ? 999 : ap) - (bp === -1 ? 999 : bp);
          return a.sort_order - b.sort_order;
        }),
    };
  },
);

export type CollectionDetail = {
  collection: {
    id: string;
    slug: string;
    title: string;
    description: string | null;
    cover_url: string | null;
  };
  items: Array<{
    id: string;
    slug: string;
    title: string;
    excerpt: string;
    image_url: string | null;
    position: number;
    from_instagram: boolean;
  }>;
};

export const getCollectionBySlug = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ slug: z.string().min(1).max(160) }).parse(d))
  .handler(async ({ data }): Promise<CollectionDetail | null> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: coll } = await supabaseAdmin
      .from("discovery_collections")
      .select("id, slug, title, description, cover_url")
      .eq("slug", data.slug)
      .neq("slug", "unclassified")
      .maybeSingle();
    if (!coll) return null;
    const { data: rows } = await supabaseAdmin
      .from("collection_items")
      .select(
        "position, discoveries!inner(id, slug, title, excerpt, image_url, is_published, hq_origin_reel_id)",
      )
      .eq("collection_id", coll.id)
      .order("position");
    const items = dedupeDiscoveries(((rows ?? []) as any[])
      .filter((r) => r.discoveries?.is_published)
      .map((r) => ({
        id: r.discoveries.id,
        slug: r.discoveries.slug,
        title: r.discoveries.title,
        excerpt: r.discoveries.excerpt,
        image_url: r.discoveries.image_url,
        position: r.position,
        from_instagram: Boolean(r.discoveries.hq_origin_reel_id),
        hq_origin_reel_id: r.discoveries.hq_origin_reel_id,
      })));
    return { collection: coll as any, items };
  });

export const getCategoryBySlug = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ slug: z.string().min(1).max(120) }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: cat, error } = await supabaseAdmin
      .from("categories")
      .select("*")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!cat) return null;
    const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const [discsRes, otherCatsRes, quizRes, viewRes] = await Promise.all([
      supabaseAdmin
        .from("discoveries")
        .select("*")
        .eq("is_published", true)
        .or(scheduledFilter())
        .eq("category_id", cat.id)
        .order("sort_order"),
      supabaseAdmin.from("categories").select("*").neq("id", cat.id).order("sort_order"),
      supabaseAdmin
        .from("quizzes")
        .select("*")
        .or(`placement.ilike.%${cat.slug}%,href.ilike.%${cat.slug}%`)
        .order("sort_order"),
      supabaseAdmin
        .from("discovery_views")
        .select("discovery_id")
        .gte("viewed_at", since)
        .limit(5000),
    ]);
    if (discsRes.error) throw new Error(discsRes.error.message);
    const discoveries = (discsRes.data ?? []) as unknown as DBDiscovery[];
    const featured = discoveries.filter((d) => d.is_featured || d.is_deep_dive);
    const latest = [...discoveries].sort((a, b) =>
      (b.created_at ?? "").localeCompare(a.created_at ?? ""),
    );
    const parseViews = (v: string | null) => {
      if (!v) return 0;
      const m = v.replace(/,/g, "").match(/([\d.]+)\s*([kKmM]?)/);
      if (!m) return 0;
      const n = parseFloat(m[1]);
      const suf = m[2].toLowerCase();
      return suf === "m" ? n * 1_000_000 : suf === "k" ? n * 1000 : n;
    };
    const popular = [...discoveries].sort(
      (a, b) => parseViews(b.views_label) - parseViews(a.views_label),
    );
    const counts = new Map<string, number>();
    for (const v of viewRes.data ?? []) {
      const id = v.discovery_id as string;
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
    const trending = discoveries
      .map((d) => ({ d, c: counts.get(d.id) ?? 0 }))
      .filter((x) => x.c > 0)
      .sort((a, b) => b.c - a.c)
      .map((x) => x.d);
    return {
      category: cat as DBCategory,
      discoveries,
      otherCategories: (otherCatsRes.data ?? []) as DBCategory[],
      quizzes: (quizRes.data ?? []) as DBQuiz[],
      featured,
      latest,
      popular,
      trending,
    };
  });

// All discoveries (lightweight) for the /discoveries hub, optional category filter + sort.
// Phase 2: is_reel / reel_hook are internal-only. Public code never reads them.
export type DiscoveryListItem = Pick<
  DBDiscovery,
  | "id"
  | "slug"
  | "title"
  | "excerpt"
  | "image_url"
  | "category_id"
  | "views_label"
  | "is_featured"
  | "created_at"
  | "sort_order"
>;

export const getAllDiscoveries = createServerFn({ method: "GET" })
  .inputValidator((d) =>
    z
      .object({
        categorySlug: z.string().min(1).max(120).optional(),
        sort: z.enum(["newest", "oldest", "popular", "trending", "random"]).default("newest"),
        q: z.string().trim().min(1).max(120).optional(),
        archetype: z.enum(["explorer", "skeptic", "dreamer", "tinkerer", "storyteller"]).optional(),
        filter: z.enum(["from-reels"]).optional(),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { ARCHETYPES } = await import("@/lib/archetypes");
    const [cats, all] = await Promise.all([
      supabaseAdmin.from("categories").select("*").order("sort_order"),
      supabaseAdmin
        .from("discoveries")
        .select(
          "id,slug,title,excerpt,body,image_url,category_id,views_label,is_featured,created_at,sort_order,hq_origin_reel_id",
        )
        .eq("is_published", true)
        .or(scheduledFilter())
        .order("created_at", { ascending: false }),
    ]);
    const categories = (cats.data ?? []) as DBCategory[];
    const bySlug = data.categorySlug
      ? (categories.find((c) => c.slug === data.categorySlug) ?? null)
      : null;
    const deriveExcerpt = (body: string | null | undefined) => {
      if (!body) return "";
      const clean = body.replace(/\s+/g, " ").trim();
      if (clean.length <= 160) return clean;
      const cut = clean.slice(0, 160);
      const lastSpace = cut.lastIndexOf(" ");
      return (lastSpace > 100 ? cut.slice(0, lastSpace) : cut).trimEnd() + "…";
    };
    const parseViews = (v: string | null) => {
      if (!v) return 0;
      const m = v.replace(/,/g, "").match(/([\d.]+)\s*([kKmM]?)/);
      if (!m) return 0;
      const n = parseFloat(m[1]);
      const suf = m[2].toLowerCase();
      return suf === "m" ? n * 1_000_000 : suf === "k" ? n * 1000 : n;
    };
    const rawRows = (all.data ?? []) as Array<
      DiscoveryListItem & { body?: string | null; hq_origin_reel_id?: string | null }
    >;
    const dedupedRows = dedupeDiscoveries(rawRows);
    let items = dedupedRows.map((d) => {
      const { body, hq_origin_reel_id: _r, ...rest } = d;
      const excerpt = rest.excerpt && rest.excerpt.trim() ? rest.excerpt : deriveExcerpt(body);
      return { ...rest, excerpt } as DiscoveryListItem;
    });
    if (data.filter === "from-reels") {
      const reelIds = new Set(dedupedRows.filter((d) => !!d.hq_origin_reel_id).map((d) => d.id));
      items = items.filter((d) => reelIds.has(d.id));
    }
    if (bySlug) items = items.filter((d) => d.category_id === bySlug.id);
    if (data.q) {
      const q = data.q.toLowerCase();
      items = items.filter(
        (d) => d.title.toLowerCase().includes(q) || (d.excerpt ?? "").toLowerCase().includes(q),
      );
    }

    if (data.sort === "oldest") {
      items = [...items].sort((a, b) => (a.created_at ?? "").localeCompare(b.created_at ?? ""));
    } else if (data.sort === "popular") {
      items = [...items].sort((a, b) => parseViews(b.views_label) - parseViews(a.views_label));
    } else if (data.sort === "trending") {
      const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data: views } = await supabaseAdmin
        .from("discovery_views")
        .select("discovery_id")
        .gte("viewed_at", since)
        .limit(5000);
      const counts = new Map<string, number>();
      for (const v of views ?? [])
        counts.set(v.discovery_id as string, (counts.get(v.discovery_id as string) ?? 0) + 1);
      items = [...items].sort((a, b) => (counts.get(b.id) ?? 0) - (counts.get(a.id) ?? 0));
    } else if (data.sort === "random") {
      items = [...items];
      for (let i = items.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [items[i], items[j]] = [items[j], items[i]];
      }
    }
    // Archetype personalization: boost items in affinity categories to the top,
    // preserving the previously selected sort within each group.
    if (data.archetype && !bySlug) {
      const affinitySlugs = ARCHETYPES[data.archetype].categoryAffinity;
      const affinityIds = new Set(
        categories.filter((c) => affinitySlugs.includes(c.slug)).map((c) => c.id),
      );
      if (affinityIds.size > 0) {
        const matched: DiscoveryListItem[] = [];
        const rest: DiscoveryListItem[] = [];
        for (const it of items) {
          (affinityIds.has(it.category_id ?? "") ? matched : rest).push(it);
        }
        items = [...matched, ...rest];
      }
    }
    return { categories, items, activeCategory: bySlug };
  });

export const getAllQuizzes = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const [quizzes, cats] = await Promise.all([
    supabaseAdmin.from("quizzes").select("*").neq("placement", "certify").order("sort_order"),
    supabaseAdmin.from("categories").select("*").order("sort_order"),
  ]);
  return {
    quizzes: dedupeQuizzes((quizzes.data ?? []) as DBQuiz[]),
    categories: (cats.data ?? []) as DBCategory[],
  };
});

export const getCategoriesHub = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const [cats, discs] = await Promise.all([
    supabaseAdmin.from("categories").select("*").order("sort_order"),
    supabaseAdmin
      .from("discoveries")
          .select("id,slug,title,excerpt,image_url,category_id,is_featured,created_at,views_label,hq_origin_reel_id")
      .eq("is_published", true)
      .or(scheduledFilter())
      .order("created_at", { ascending: false }),
  ]);
  return {
    categories: (cats.data ?? []) as DBCategory[],
    discoveries: dedupeDiscoveries((discs.data ?? []) as DBDiscovery[]),
  };
});

// Log one view for an article. Anonymous insert via service role (RLS allows anon too).
// `sessionId` lets the caller dedupe per session at the server side later if needed.
export const logDiscoveryView = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        slug: z.string().min(1).max(200),
        sessionId: z.string().min(1).max(64).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: disc } = await supabaseAdmin
      .from("discoveries")
      .select("id")
      .eq("slug", data.slug)
      .eq("is_published", true)
      .maybeSingle();
    if (!disc) return { ok: false as const };
    await supabaseAdmin.from("discovery_views").insert({
      discovery_id: disc.id,
      session_id: data.sessionId ?? null,
    });
    return { ok: true as const };
  });

export type TrendingItem = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  image_url: string | null;
  category_id: string | null;
  views_label: string | null;
  view_count_7d: number;
};

// Trending = most viewed in last 7 days. Falls back to recency-weighted ordering
// (featured first, then newest) when there are too few view events.
export const getTrendingDiscoveries = createServerFn({ method: "GET" })
  .inputValidator((d) =>
    z
      .object({
        limit: z.number().int().min(1).max(20).default(5),
        excludeSlug: z.string().min(1).max(200).optional(),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data }): Promise<{ items: TrendingItem[]; source: "views" | "fallback" }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

    // Pull recent view events (cap at 5k for safety) and aggregate in-process.
    const { data: views } = await supabaseAdmin
      .from("discovery_views")
      .select("discovery_id")
      .gte("viewed_at", since)
      .limit(5000);

    const counts = new Map<string, number>();
    for (const v of views ?? []) {
      counts.set(v.discovery_id as string, (counts.get(v.discovery_id as string) ?? 0) + 1);
    }

    // Fetch all currently-publishable articles once; we filter/order in JS.
    const { data: pubs } = await supabaseAdmin
      .from("discoveries")
      .select(
        "id,slug,title,excerpt,image_url,category_id,views_label,is_featured,sort_order,created_at,hq_origin_reel_id",
      )
      .eq("is_published", true)
      .or(scheduledFilter())
      .order("created_at", { ascending: false });
    const all = (pubs ?? []) as Array<{
      id: string;
      slug: string;
      title: string;
      excerpt: string;
      image_url: string | null;
      category_id: string | null;
      views_label: string | null;
      is_featured: boolean;
      sort_order: number;
      created_at: string;
      hq_origin_reel_id?: string | null;
    }>;

    const parseViews = (v: string | null) => {
      if (!v) return 0;
      const m = v.replace(/,/g, "").match(/([\d.]+)\s*([kKmM]?)/);
      if (!m) return 0;
      const n = parseFloat(m[1]);
      const suf = m[2].toLowerCase();
      return suf === "m" ? n * 1_000_000 : suf === "k" ? n * 1000 : n;
    };
    const deduped = dedupeDiscoveries(all);

    const eligible = data.excludeSlug
      ? deduped.filter((d) => d.slug !== data.excludeSlug)
      : deduped;

    // Real trending: anything with at least one view in window, sorted by count desc.
    const withCounts = eligible
      .map((d) => ({ d, c: counts.get(d.id) ?? 0 }))
      .filter((x) => x.c > 0)
      .sort((a, b) => b.c - a.c || (b.d.is_featured ? 1 : 0) - (a.d.is_featured ? 1 : 0));

    const minReal = 3;
    const useViews = withCounts.length >= minReal;

    let picked: typeof eligible;
    if (useViews) {
      picked = withCounts.slice(0, data.limit).map((x) => x.d);
    } else {
      // Fallback: featured first, then newest. Backfill remainder by recency.
      const seen = new Set<string>();
      const seed = [
        ...eligible.filter((d) => d.is_featured),
        ...eligible, // already newest-first from query
      ].filter((d) => (seen.has(d.id) ? false : (seen.add(d.id), true)));
      picked = seed.slice(0, data.limit);
    }

    return {
      source: useViews ? "views" : "fallback",
      items: picked.map((d) => ({
        id: d.id,
        slug: d.slug,
        title: d.title,
        excerpt: d.excerpt,
        image_url: d.image_url,
        category_id: d.category_id,
        views_label: d.views_label,
        view_count_7d: counts.get(d.id) ?? 0,
      })),
    };
  });

export const getQuizBySlug = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ slug: z.string().min(1).max(120) }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: q, error } = await supabaseAdmin
      .from("quizzes")
      .select("*")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (q ?? null) as DBQuiz | null;
  });

export const getRandomQuiz = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ excludeSlug: z.string().max(120).optional() }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let query = supabaseAdmin.from("quizzes").select("slug,question");
    if (data.excludeSlug) query = query.neq("slug", data.excludeSlug);
    const { data: rows, error } = await query.limit(500);
    if (error) throw new Error(error.message);
    const list = (rows ?? []) as Array<{ slug: string; question: string }>;
    if (!list.length) return null;
    return list[Math.floor(Math.random() * list.length)];
  });

export const getRandomDiscovery = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ excludeSlug: z.string().max(160).optional() }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const now = new Date().toISOString();
    let query = supabaseAdmin
      .from("discoveries")
      .select("slug,title")
      .eq("is_published", true)
      .or(`publish_at.is.null,publish_at.lte.${now}`);
    if (data.excludeSlug) query = query.neq("slug", data.excludeSlug);
    const { data: rows, error } = await query.limit(1000);
    if (error) throw new Error(error.message);
    const list = (rows ?? []) as Array<{ slug: string; title: string }>;
    if (!list.length) return null;
    return list[Math.floor(Math.random() * list.length)];
  });

export type SearchResult =
  | { kind: "discovery"; slug: string; title: string; excerpt: string; image_url: string | null }
  | { kind: "category"; slug: string; title: string; excerpt: string; image_url: string | null }
  | { kind: "quiz"; slug: string; title: string; excerpt: string; image_url: string | null };

export const searchContent = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ q: z.string().trim().min(1).max(120) }).parse(d))
  .handler(async ({ data }): Promise<{ results: SearchResult[] }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const q = data.q.replace(/[%,]/g, " ").trim();
    if (!q) return { results: [] };
    const like = `%${q}%`;
    const [discs, cats, quizs] = await Promise.all([
      supabaseAdmin
        .from("discoveries")
        .select("id,slug,title,excerpt,image_url,publish_at,created_at,hq_origin_reel_id")
        .eq("is_published", true)
        .or(`title.ilike.${like},excerpt.ilike.${like},body.ilike.${like}`)
        .limit(10),
      supabaseAdmin
        .from("categories")
        .select("slug,title,label,blurb,cover_url")
        .or(`title.ilike.${like},label.ilike.${like},blurb.ilike.${like}`)
        .limit(5),
      supabaseAdmin.from("quizzes").select("slug,question").ilike("question", like).limit(5),
    ]);
    const now = new Date().toISOString();
    const results: SearchResult[] = [
       ...dedupeDiscoveries((discs.data ?? []) as any[])
        .filter((d: any) => !d.publish_at || d.publish_at <= now)
        .map((d: any) => ({
          kind: "discovery" as const,
          slug: d.slug,
          title: d.title,
          excerpt: d.excerpt ?? "",
          image_url: d.image_url ?? null,
        })),
      ...(cats.data ?? []).map((c: any) => ({
        kind: "category" as const,
        slug: c.slug,
        title: c.title || c.label,
        excerpt: c.blurb ?? "",
        image_url: c.cover_url ?? null,
      })),
       ...dedupeQuizzes((quizs.data ?? []) as DBQuiz[]).map((q: any) => ({
        kind: "quiz" as const,
        slug: q.slug,
        title: q.question,
        excerpt: "",
        image_url: null,
      })),
    ];
    return { results };
  });

export type JourneyItem = {
  slug: string;
  title: string;
  excerpt: string;
  image_url: string | null;
};

/** Published discoveries by slug - used to hydrate scroll journeys. */
export const getDiscoveriesBySlugs = createServerFn({ method: "GET" })
  .inputValidator((d) =>
    z.object({ slugs: z.array(z.string().min(1).max(200)).min(1).max(60) }).parse(d),
  )
  .handler(async ({ data }): Promise<JourneyItem[]> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows } = await supabaseAdmin
      .from("discoveries")
      .select("slug, title, excerpt, image_url, is_published")
      .in("slug", data.slugs);
    return ((rows ?? []) as any[])
      .filter((r) => r.is_published)
      .map((r) => ({ slug: r.slug, title: r.title, excerpt: r.excerpt, image_url: r.image_url }));
  });
